/**
 * The IndexedDB backend.
 *
 * Two decisions shape this file, and both are about being able to trust it.
 *
 * **The factory is injected.** `openIndexedDbStore` takes an `IdbFactoryLike`
 * and only falls back to `globalThis.indexedDB`. A backend that can only run
 * inside a browser can only be tested inside a browser, and a persistence layer
 * whose failure mode is "the user's data did not survive a reload" is the last
 * place to accept a test suite nobody runs.
 *
 * **The surface is declared, not imported.** This package compiles without the
 * DOM lib, so the slice of IndexedDB used here is written out below. That is
 * not a workaround — naming the seven calls this adapter makes is what makes
 * the injection point small enough to be worth injecting.
 *
 * The transaction discipline is the other thing worth reading for: everything a
 * commit does is issued synchronously inside one `readwrite` transaction and
 * resolved on `oncomplete`. An IndexedDB transaction dies on the first non-IDB
 * `await`, which is precisely why `StorePersistence.commit` takes a whole patch
 * instead of exposing an interactive transaction.
 */

import { StoreEngine } from './engine.js';
import type {
  CommitBasis, CommitOutcome,
  PersistedEdge, PersistedJournal, PersistedKey, PersistedManifest, PersistedObject,
  PersistedSnapshot, PersistPatch, StorePersistence,
} from './persistence.js';
import type { ObjectStore, StoreOptions } from './store.js';
import {
  digestOf, acceptedMime, isAcceptedMime,
  type BlobId, type BlobRef, type BlobLookup, type BlobStore, type StoredBlob,
} from './blobs.js';
import {
  derivativeIndexOver, sameDerivative,
  type DerivativeEntry, type DerivativeIndex,
} from './derivatives.js';

/* ─────────────────────────── the slice of IndexedDB this adapter touches */

export interface IdbRequestLike<T> {
  result: T;
  onsuccess: ((this: unknown, ev: unknown) => void) | null;
  onerror: ((this: unknown, ev: unknown) => void) | null;
  error?: { message?: string } | null;
}

export interface IdbObjectStoreLike {
  put(value: unknown): IdbRequestLike<unknown>;
  delete(key: string): IdbRequestLike<unknown>;
  getAll(): IdbRequestLike<unknown[]>;
  /** One record by key. The graph stores never need this — they are read
   *  wholesale on open — but a blob is exactly the thing you fetch one of. */
  get(key: string): IdbRequestLike<unknown>;
  /**
   * Is there a record under this key — WITHOUT reading it.
   *
   * @Codex, P2 on `384b5df`, twice: a presence question was answered with
   * `get()`, which structured-clones a 64 MiB payload to learn a boolean, and
   * another with `getAll()`, which clones every accepted row to find one
   * digest. Both sit on the ingest path `e2e/ingest-budget.mjs` measures.
   *
   * `count` is the platform's own key-only read, and it is the smallest thing
   * that answers this. `getKey` would do as well; `count` is chosen because a
   * fake can implement it without inventing key semantics.
   */
  count(key: string): IdbRequestLike<number>;
}

export interface IdbTransactionLike {
  objectStore(name: string): IdbObjectStoreLike;
  oncomplete: ((this: unknown, ev: unknown) => void) | null;
  onerror: ((this: unknown, ev: unknown) => void) | null;
  onabort: ((this: unknown, ev: unknown) => void) | null;
  error?: { message?: string } | null;
}

export interface IdbDatabaseLike {
  readonly objectStoreNames: { contains(name: string): boolean };
  createObjectStore(name: string, options: { keyPath: string }): IdbObjectStoreLike;
  transaction(names: readonly string[], mode: 'readonly' | 'readwrite'): IdbTransactionLike;
  close(): void;
  /** Fired on THIS connection when another one wants a newer version. Holding
   *  on is what blocks the other tab, so the obligation is to let go. */
  onversionchange?: ((this: unknown, ev: unknown) => void) | null;
}

export interface IdbOpenRequestLike extends IdbRequestLike<IdbDatabaseLike> {
  onupgradeneeded: ((this: unknown, ev: unknown) => void) | null;
  /** Fired when another connection still holds this database at an older
   *  version, so the upgrade cannot start. Optional on the type because a
   *  caller that never bumps a version never sees one — but a caller that does
   *  and ignores it waits forever, which is the whole finding below. */
  onblocked?: ((this: unknown, ev: unknown) => void) | null;
}

export interface IdbFactoryLike {
  open(name: string, version?: number): IdbOpenRequestLike;
}

/* ────────────────────────────────────────────────────────────── the store */

const STORES = ['objects', 'keys', 'edges', 'manifests', 'journal', 'meta'] as const;

/**
 * The one row in `meta` that every commit reads and writes.
 *
 * Decision 0027's boundary lives here: two tabs share one database, so the only
 * place a comparison means anything is inside the same `readwrite` transaction
 * that writes the batch. An engine-local check passes at exactly the moment it
 * is most wrong — the other tab has already committed and this one has not
 * noticed.
 */
const REVISION_ROW = 'revision';

/**
 * Blobs, and their metadata, in two stores that are written together.
 *
 * NOT in `STORES`, and that is the whole point of them being separate. `load()`
 * reads every record in every store in that list on open; a photograph pool
 * would pull every image into memory to show a list of titles.
 *
 * Two stores rather than one because `stat` and `list` have to answer "how big
 * is this, and what is it" WITHOUT reading the bytes, and IndexedDB hands back
 * whole records. One store would make `list()` on a store holding a gigabyte of
 * video read a gigabyte of video. They are written in a single transaction, so
 * they cannot drift — the same discipline `commit` already uses for the graph.
 */
const BLOB_BYTES = 'blob-bytes';
const BLOB_META = 'blob-meta';

/**
 * Which transform produced which blob, keyed by decision 0041's encoded key.
 *
 * A third store rather than rows in `blob-meta`, for the reason those two are
 * already split: `list()` reads every record in the store it names, and a
 * source with entries for four transform versions would then make asking "what
 * is in here" read four rows it did not want. Also they are keyed differently
 * — a derivative key is not a blob id, and a store cannot have two key paths.
 */
const DERIVATIVES = 'blob-derivatives';

/**
 * The accepted TYPED rows — decision `0047`'s second logical record.
 *
 * A fourth store rather than a re-keying of `blob-meta`, because changing a
 * store's `keyPath` means deleting and recreating it, which throws away every
 * row a person already has. This one is keyed by `${id};${mime}` and the old
 * store's rows are copied into it during the upgrade, so nothing is lost and
 * `blob-meta` is simply no longer written.
 */
const BLOB_REFS = 'blob-refs';

/** `${digest};${canonical mime}` — the pair `0047` keys an accepted reference
 *  by. Spelled once so the migration and the writes cannot drift. */
const refKey = (id: string, mime: string) => `${id};${mime}`;

/** Version 2 adds the two blob stores; version 4 adds the derivative index.
 *  `onupgradeneeded` creates whatever is missing, so a database written by an
 *  older version gains them and keeps its objects; nothing here migrates a
 *  record, because nothing about the graph changed and a derivative index is
 *  rebuildable metadata — an empty one costs a decode, not a fact. */
const DB_VERSION = 5;

/** `manifests` is keyed by `ref` and everything else by `id`. Written out
 *  rather than inferred, because a wrong key path silently overwrites every
 *  record with the last one. */
const KEY_PATH: Record<(typeof STORES)[number], string> = {
  objects: 'id', keys: 'id', edges: 'id', manifests: 'ref', journal: 'id', meta: 'id',
};

function promisify<T>(req: IdbRequestLike<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(new Error(req.error?.message ?? 'IndexedDB request failed'));
  });
}

/**
 * How long a `blocked` event is given before it is read as a stuck tab.
 *
 * Long enough for an ordinary transaction to commit and release the connection
 * a `close()` has already been called on, and short enough that a tab which is
 * genuinely frozen still produces a message a person can act on rather than a
 * boot that never ends. Exported so a clause can drive both sides of it rather
 * than waiting out a real three seconds.
 */
export const BLOCKED_GRACE_MS = 3_000;

export class IndexedDbPersistence implements StorePersistence {
  private constructor(private readonly db: IdbDatabaseLike) {}

  /**
   * Open at `DB_VERSION`, and refuse to wait forever for a tab that will not
   * let go.
   *
   * @Codex, P1 on #184: *"When a user still has a pre-v4 Morph tab open,
   * requesting version 4 here leaves `indexedDB.open()` blocked until the old
   * connection closes. This adapter defines neither an `onblocked` handler nor
   * a database `onversionchange` handler, and the boot path has no timeout, so
   * a newly opened or refreshed tab can remain indefinitely in an unresolved
   * boot instead of displaying the boot-failure UI."*
   *
   * Correct, and it became reachable the moment `DB_VERSION` moved to 4 — the
   * first bump since the blob stores. Both halves are needed and they do
   * different jobs:
   *
   * - **`onversionchange`, on every connection this opens.** An older tab that
   *   holds on is what blocks the newer one, so the obligation is to let go.
   *   This is the half that makes the ordinary case — one tab open, another
   *   reloaded onto a new build — resolve by itself.
   * - **`onblocked`, on the open.** For when letting go is not enough: a tab
   *   frozen by the browser, or one whose page script has stopped running.
   *   Then the open REJECTS with a sentence naming the cause, which reaches
   *   the boot-failure surface. A named refusal is strictly better than a page
   *   that never finishes loading — the same argument task `0112` made for
   *   bounding `settled()`.
   */
  static async open(factory: IdbFactoryLike, dbName: string): Promise<IndexedDbPersistence> {
    const req = factory.open(dbName, DB_VERSION);
    req.onupgradeneeded = () => {
      for (const name of STORES) {
        if (!req.result.objectStoreNames.contains(name)) {
          req.result.createObjectStore(name, { keyPath: KEY_PATH[name] });
        }
      }
      for (const name of [BLOB_BYTES, BLOB_META]) {
        if (!req.result.objectStoreNames.contains(name)) {
          req.result.createObjectStore(name, { keyPath: 'id' });
        }
      }
      if (!req.result.objectStoreNames.contains(DERIVATIVES)) {
        req.result.createObjectStore(DERIVATIVES, { keyPath: 'key' });
      }
      /**
       * Version 5, decision `0047`: typed rows, migrated from the digest-keyed
       * ones.
       *
       * *"The IndexedDB upgrade creates typed metadata rows from existing
       * digest-only metadata without rewriting blob bytes … Migration must be
       * idempotent and must not guess additional interpretations from file
       * extensions, roles, magic bytes, or derivative metadata. It preserves
       * the one media type the old store actually recorded and nothing more."*
       *
       * Idempotent by construction: the copy runs only in the branch that
       * CREATES the store, so a second upgrade finds it present and does
       * nothing. Guessing is impossible here rather than merely avoided —
       * the only input is the row the old store already held.
       *
       * `blob-bytes` is untouched, which is the point: a person with a vault of
       * video pays nothing for this.
       */
      if (!req.result.objectStoreNames.contains(BLOB_REFS)) {
        const refs = req.result.createObjectStore(BLOB_REFS, { keyPath: 'key' });
        if (req.result.objectStoreNames.contains(BLOB_META)) {
          // Read through the SAME versionchange transaction. A separate one
          // would not exist yet — the upgrade owns the connection until it
          // finishes — and would also race the first write after it.
          const tx = (req as unknown as { transaction?: IdbTransactionLike }).transaction;
          const old = tx?.objectStore(BLOB_META);
          const all = old?.getAll?.();
          if (all) {
            all.onsuccess = () => {
              for (const row of (all.result ?? []) as BlobRef[]) {
                if (!row || typeof row.mime !== 'string') continue;
                /**
                 * A malformed historical type stays on the LEGACY path.
                 *
                 * @Codex, P2 on `857dbfa`: *"When upgrading a pre-v5 vault
                 * whose old public `put()` accepted a noncanonical MIME such
                 * as `IMAGE/PNG` or `image/png; charset=utf-8`, this
                 * unconditional copy publishes that value into `blob-refs`.
                 * `list()` and `stat()` then expose an allegedly accepted
                 * typed `BlobRef` that `blobRef()` refuses to encode and
                 * `blobRefIn()` can never select."*
                 *
                 * `acceptedMime` went into `put` this morning, which stops new
                 * rows like this — and a migration is the one door that
                 * predates its own validator, so it was carrying forward
                 * exactly the values the validator exists to refuse.
                 *
                 * A typed row that cannot be referenced is worse than no typed
                 * row: `blobRefIn` can never select it, so it is a promise
                 * `list()` and `stat()` make and nothing can keep. Left out,
                 * the bytes are still reachable by the bare digest through the
                 * legacy path that has always resolved them — which is where
                 * they were before this migration existed.
                 *
                 * NOT canonicalized on the way past. `IMAGE/PNG` is probably
                 * `image/png`, and `probably` is a guess about somebody's file
                 * written into a store whose whole contract is that a
                 * reference means one row.
                 */
                if (!isAcceptedMime(row.mime)) continue;
                refs.put({ key: refKey(row.id, row.mime), ...row });
              }
            };
          }
        }
      }
    };
    /**
     * A refused open must not leave a connection behind it.
     *
     * @Codex, P2 on #184: *"When `onblocked` wins this race, it rejects the
     * boot but does not cancel the IndexedDB request; if the older tab
     * subsequently closes, the request can still succeed, leaving an untracked
     * connection that never receives the `onversionchange` closer below. That
     * failed-boot page can then block a later schema upgrade itself."*
     *
     * Which is the defect this whole block was written to remove, one turn
     * later and pointed the other way: the page that could not open the
     * database becomes the page that stops anybody else opening it. There is no
     * cancel in the API, so the only remedy is to let the open finish and close
     * what it hands back.
     */
    /**
     * `blocked` STARTS a clock; it is not the verdict.
     *
     * @Codex, P2 on `dae7f0d`: *"When an older responsive tab handles
     * `versionchange` by calling `close()` while it still has an active
     * transaction, the connection remains open until that transaction finishes,
     * so the new open can emit `blocked` and then proceed normally moments
     * later. This handler irrevocably rejects on the transient event and marks
     * the eventual successful connection for closure, making an ordinary
     * upgrade display the 'other tab is not responding' boot failure and
     * require a reload."*
     *
     * Exact, and it is the same mistake as the one the paragraph below repairs,
     * arriving from the other side: that one treated a refusal as final for the
     * REQUEST, this one treats one event as final for the TAB. `close()` on a
     * connection with work in flight is a promise to go, not a departure — the
     * connection lives until its last transaction commits — so a tab doing
     * exactly the right thing produces this event.
     *
     * The wait is bounded because the case it replaced is real: a tab frozen by
     * the browser never runs `onversionchange` at all, and `0112`'s rule is
     * that a named refusal beats a wait that cannot end. So the refusal is the
     * same, and only its timing moves.
     */
    let refused = false;
    let deadline: ReturnType<typeof setTimeout> | undefined;
    const blocked = new Promise<never>((_, reject) => {
      req.onblocked = () => {
        deadline = setTimeout(() => {
          refused = true;
          reject(new Error(
            `Morph is open in another tab at an older version, and that tab is not `
            + `responding. Close Morph's other tabs and reload.`));
        }, BLOCKED_GRACE_MS);
      };
    });
    // Nothing observes `blocked` once the race is over, and a rejection nobody
    // is listening to is an unhandled rejection in the page. The timer is
    // cleared below on the ordinary path; this covers the ordering where it is
    // not, and costs one no-op handler.
    void blocked.catch(() => { /* the race already answered */ });
    // ONE `promisify`, reused. It assigns `req.onsuccess`, so calling it twice
    // silently replaces the first handler and leaves that promise pending
    // forever — which would make the race below unresolvable in the ordinary
    // case, to guard against the rare one.
    const opened = promisify(req);
    void opened.then((late) => { if (refused) late.close(); }, () => { /* already refused */ });
    const db = await Promise.race([opened, blocked]);
    // The open won, so the holder let go inside the grace. Clearing this is
    // what keeps a late refusal from firing after a successful boot.
    if (deadline !== undefined) clearTimeout(deadline);
    // Assigned on EVERY connection, not only on one that upgraded: the tab that
    // has to let go is the OLD one, and it has no idea a newer version exists
    // until this fires. A connection that closes here leaves the page's later
    // reads failing, which is correct and visible — the alternative is another
    // tab that never boots.
    db.onversionchange = () => { db.close(); };
    return new IndexedDbPersistence(db);
  }

  /** The blob half of the same database, sharing the same connection. */
  blobs(): BlobStore {
    return new IndexedDbBlobs(this.db);
  }

  async load(): Promise<PersistedSnapshot> {
    const tx = this.db.transaction(STORES, 'readonly');
    // Every getAll is issued before the first await, so the transaction is
    // still alive when they resolve. Awaiting one at a time would let it close.
    // The revision is read in the SAME transaction as the rows, or a load could
    // report a revision that does not describe the state it just read.
    const pending = STORES.map((name) => promisify(tx.objectStore(name).getAll()));
    const [objects, keys, edges, manifests, journal, meta] = await Promise.all(pending);
    const row = ((meta ?? []) as { id: string; revision?: number }[])
      .find((entry) => entry.id === REVISION_ROW);
    return {
      objects: (objects ?? []) as PersistedObject[],
      keys: (keys ?? []) as PersistedKey[],
      edges: (edges ?? []) as PersistedEdge[],
      manifests: (manifests ?? []) as PersistedManifest[],
      journal: (journal ?? []) as PersistedJournal[],
      revision: row?.revision ?? 0,
    };
  }

  /** One transaction, every write issued synchronously, resolved on
   *  `oncomplete` — not on the last request's `onsuccess`, which fires before
   *  the transaction has actually committed and would report durability the
   *  store does not have. */
  async commit(patch: PersistPatch, basis: CommitBasis): Promise<CommitOutcome> {
    const tx = this.db.transaction(STORES, 'readwrite');
    /**
     * READ THE REVISION FIRST, INSIDE THIS TRANSACTION.
     *
     * Not before it, and not from the engine's own memory: an IndexedDB
     * `readwrite` transaction is serialized against every other one on these
     * stores, so a comparison made here cannot be overtaken, and a comparison
     * made anywhere else can. That is the whole of decision 0027's backend
     * obligation — *"IndexedDB performs them in one read-write transaction
     * shared by every engine that opens the database"*.
     *
     * The `await` is safe because it resolves from a request issued in this
     * same transaction; a transaction dies on the first NON-IDB await, which is
     * why nothing else may come between.
     */
    type RevisionRow = { readonly id: string; readonly revision?: number };
    const row = await promisify<unknown>(tx.objectStore('meta').get(REVISION_ROW)) as RevisionRow | undefined;
    const current = row?.revision ?? 0;
    if (current !== basis.expect) {
      // Nothing has been written yet, so letting the transaction finish empty
      // is the same as aborting it and does not race the abort event.
      return { ok: false, current };
    }
    const put = (name: (typeof STORES)[number], rows: readonly unknown[] | undefined) => {
      if (!rows?.length) return;
      const store = tx.objectStore(name);
      for (const row of rows) store.put(row);
    };
    const del = (name: (typeof STORES)[number], ids: readonly string[] | undefined) => {
      if (!ids?.length) return;
      const store = tx.objectStore(name);
      for (const id of ids) store.delete(id);
    };

    put('objects', patch.objects);
    del('objects', patch.deletedObjects);
    put('keys', patch.keys);
    del('keys', patch.deletedKeys);
    put('edges', patch.edges);
    del('edges', patch.deletedEdges);
    put('manifests', patch.manifests);
    put('journal', patch.journal);
    // Advanced in the same transaction that wrote the batch. A separate write
    // would let a crash leave the rows and the revision disagreeing, which is
    // the one state no later comparison could detect.
    tx.objectStore('meta').put({ id: REVISION_ROW, revision: basis.next });

    await settled(tx);
    return { ok: true };
  }

  async close(): Promise<void> {
    this.db.close();
  }
}

/**
 * Bytes on disk, keyed by their own digest.
 *
 * Decision 0017. Reads and writes go through the same two stores in one
 * transaction each, so metadata and bytes cannot disagree about what exists.
 *
 * The bytes are stored as a plain `Uint8Array`. IndexedDB structured-clones it,
 * which means the copy on the way in that `createMemoryBlobs` has to make by
 * hand happens here for free — and the copy on the way OUT does too, so a
 * caller that writes into what it got back cannot reach the stored record.
 */
class IndexedDbBlobs implements BlobStore {
  /** The same `derivativeIndexOver` the memory backend uses, over rows in
   *  `blob-derivatives`. Decision 0041 requires both backends to expose one
   *  contract; the way to get that is for there to be one implementation of the
   *  rules and two of the storage, rather than two of each. */
  readonly derivatives: DerivativeIndex;

  constructor(private readonly db: IdbDatabaseLike) {
    this.derivatives = derivativeIndexOver(
      {
        get: async (encoded) => {
          const tx = this.db.transaction([DERIVATIVES], 'readonly');
          const row = await promisify(tx.objectStore(DERIVATIVES).get(encoded));
          return (row as { mapping?: DerivativeEntry } | undefined)?.mapping;
        },
        /**
         * The comparison and the write in ONE transaction.
         *
         * The same shape `commit` uses for the revision row, and for the same
         * reason: an IndexedDB `readwrite` transaction is serialized against
         * every other one on this store, so a comparison made inside it cannot
         * be overtaken and a comparison made anywhere else can. Two tabs
         * publishing different derivatives for one key is the case @Codex
         * raised, and a `get` in one transaction followed by a `put` in another
         * is precisely how both of them see an empty row.
         *
         * The `await` is safe because it resolves from a request issued in this
         * same transaction; a transaction dies on the first NON-IDB await,
         * which is why nothing else may come between.
         */
        putIf: async (encoded, expected, expectedPresent, entry) => {
          // The blob rows in the same transaction, so the presence the caller
          // decided on cannot change between the decision and the write —
          // @Codex's P2 on #184. A `readwrite` transaction is serialized
          // against every other one on these stores, which is the only place
          // that guarantee exists.
          //
          // BLOB_REFS since decision `0047`, not BLOB_META. Reading the store
          // that is no longer written made this compare `false` against a
          // `expectedPresent` of `true` on every attempt, so `publish` exhausted
          // its retries and threw *"changed under 8 attempts"* — a symptom
          // several steps from its cause, and caught by an existing clause
          // rather than by a new one.
          //
          // Presence is a DIGEST question: any accepted type for those bytes
          // means the derived blob is there. `stat` asks it the same way, and
          // the two must agree or this comparison is against a different fact
          // than the one the caller decided on.
          const tx = this.db.transaction([DERIVATIVES, BLOB_BYTES], 'readwrite');
          const store = tx.objectStore(DERIVATIVES);
          if (expected) {
            /**
             * The BYTE record, by key. @Codex, P2 on `384b5df`: *"this presence
             * check loads and structured-clones every accepted blob-reference
             * row merely to find one digest, making each publication O(total
             * blobs) in both time and memory. This runs on the ingest path
             * after every new derivative, so a vault with tens of thousands of
             * media rows can incur a growing main-thread pause for a
             * constant-time existence question."*
             *
             * `blob-bytes` is keyed by the digest, so this is that question
             * asked in one lookup — and it is also the question the MEMORY
             * backend asks, `bytes.has(id)`. The comment above requires the two
             * to agree, and scanning typed rows agreed only because `delete`
             * happens to remove both. Asking the byte record makes the
             * agreement structural instead of incidental.
             */
            const there = (await promisify(
              tx.objectStore(BLOB_BYTES).count(expected.derived))) > 0;
            if (there !== expectedPresent) { await settled(tx); return false; }
          }
          const row = await promisify(store.get(encoded));
          const now = (row as { mapping?: DerivativeEntry } | undefined)?.mapping?.record;
          // The WHOLE mapping, not its derived id — see `putIf`'s contract. Two
          // transform versions may legitimately point at one blob, so the id is
          // the field most likely to be equal across two different mappings.
          const unchanged = (now === undefined) === (expected === undefined)
            && (!now || !expected || sameDerivative(now, expected));
          if (!unchanged) {
            // Letting an empty transaction finish is the same as aborting it
            // and does not race the abort event — `commit` says so too.
            await settled(tx);
            return false;
          }
          store.put({ key: encoded, mapping: entry });
          await settled(tx);
          return true;
        },
        delete: async (encoded) => {
          const tx = this.db.transaction([DERIVATIVES], 'readwrite');
          tx.objectStore(DERIVATIVES).delete(encoded);
          await settled(tx);
        },
        all: async () => {
          const tx = this.db.transaction([DERIVATIVES], 'readonly');
          const rows = (await promisify(tx.objectStore(DERIVATIVES).getAll())) ?? [];
          return (rows as { mapping: DerivativeEntry }[]).map((r) => r.mapping);
        },
      },
      // Presence through `blob-meta`, which is the store whose whole job is
      // answering that without reading bytes. A stale mapping is one whose
      // derived blob was deleted, and this is what notices.
      (id) => this.stat(id),
    );
  }

  async put(bytes: Uint8Array, mime: string): Promise<BlobRef> {
    /**
     * BEFORE the hash, and before anything durable.
     *
     * @Codex, P2 on `be5b9ec`: a noncanonical MIME reached the row and the
     * bytes, and only `blobRef()` refused it afterwards — *"on IndexedDB the
     * unusable row and bytes are already durable"*. Placement is the whole fix:
     * a refusal after the write leaves exactly the orphan the finding
     * describes.
     */
    acceptedMime(mime);
    const id = await digestOf(bytes);
    // Both stores in ONE transaction. Written separately, a crash between them
    // leaves metadata describing bytes that are not there, and `stat` — whose
    // entire job is answering without reading — would confidently say yes.
    const tx = this.db.transaction([BLOB_BYTES, BLOB_META, BLOB_REFS], 'readwrite');
    const meta = tx.objectStore(BLOB_REFS);
    /**
     * The FIRST mime wins, and the second put writes nothing.
     *
     * @Codex, P2 on #184: *"When identical bytes are ingested again under a
     * different accepted MIME — for example, an MP4 first as `audio/mp4` and
     * later as `video/mp4` — this unconditional metadata put overwrites the
     * MIME for the shared content digest. Existing `morph-blob:` references are
     * resolved using the store's MIME, so the later ingest retroactively
     * changes every earlier reference, while the memory backend retains the
     * first MIME and produces different behavior."*
     *
     * Both halves are right, and the second is what settles which way to fix
     * it. `blobs.ts` says of `put`: *"Idempotent: the same bytes yield the same
     * id and the second call writes nothing."* The memory backend does exactly
     * that — `if (!existing) held.set(...)` — and this one did not. The
     * contract and the other implementation already agreed; this was the
     * outlier, so it is the one that changes.
     *
     * Read inside the same transaction, so nothing can land between the look
     * and the write.
     */
    // Cast at the read rather than as a type argument to `promisify`.
    // `get` is declared `IdbRequestLike<unknown>`, and `IdbRequestLike` carries
    // an OPTIONAL `error?`, so under `exactOptionalPropertyTypes` it is not
    // assignable to `IdbRequestLike<BlobRef | undefined>` — TS2379, on the
    // optional property rather than on `result`. `stat` and `all` below already
    // read this store the same way.
    const held = (await promisify(meta.get(refKey(id, mime)))) as BlobRef | undefined;
    if (held) {
      // Nothing is written, which is the contract `blobs.ts` states and the
      // memory backend keeps. Re-putting the bytes would also be the exact
      // blocking work `e2e/ingest-budget.mjs` measures, paid for a second time
      // to store what is already there byte for byte.
      await settled(tx);
      // Stripped, because a row read back carries the composite storage key
      // and the first `put` of the same bytes does not. See `plainRef`.
      return plainRef(held)!;
    }
    /**
     * The BYTES are written once; the ROW is written per accepted type.
     *
     * `0047`: *"Repeating the same bytes with a different accepted media type
     * publishes another metadata row and returns a reference for that row
     * without rewriting the byte record or either earlier row."* Which is why
     * the byte write is guarded separately from the row write — a second type
     * for a 64 MiB source must not re-clone it, and that is the same blocking
     * work `e2e/ingest-budget.mjs` measures.
     */
    const ref: BlobRef = { id, mime, bytes: bytes.byteLength };
    /**
     * `count`, not `get`. @Codex, P2 on `384b5df`: *"the exact typed row is
     * absent, so this `get()` structured-clones the entire existing
     * `Uint8Array` merely to test whether it exists. The incoming 64 MiB source
     * is still resident as well, needlessly doubling transient memory and
     * adding a full byte-proportional read to what should be a metadata-only
     * second acceptance."*
     *
     * The comment two lines up already says a second type *"must not re-clone
     * it, and that is the same blocking work `e2e/ingest-budget.mjs`
     * measures"* — and then the existence check cloned it anyway, on the read
     * side instead of the write side. Right rule, one door along.
     */
    const already = await promisify(tx.objectStore(BLOB_BYTES).count(id));
    if (!already) {
      tx.objectStore(BLOB_BYTES).put({ id, bytes });
      /**
       * The FIRST accepted type for these bytes, recorded as what a bare digest
       * means. Written only alongside the first byte write, so a later type
       * cannot claim it — which is the whole property @Codex's P2 was about.
       *
       * This keeps `blob-meta` meaningful for digests first seen AFTER the v5
       * upgrade, so the legacy path is one rule rather than "migrated vaults
       * behave differently".
       */
      tx.objectStore(BLOB_META).put(ref);
    }
    meta.put({ key: refKey(id, mime), ...ref });
    await settled(tx);
    return ref;
  }

  /**
   * The row a lookup selects — the exact pair, or a legacy digest.
   *
   * A bare `BlobId` scans, because the fake IndexedDB these tests drive has no
   * index support and teaching it one is a feature, not a fixture. The rows are
   * metadata — id, mime, byte length — so this reads kilobytes where `list()`
   * already reads the same, and never touches `blob-bytes`. An index on `id` is
   * the right shape once the fake can express it, and would not change the
   * contract.
   */
  private async rowFor(at: BlobLookup): Promise<BlobRef | undefined> {
    /**
     * ONE transaction over BOTH stores, and this is a browser fact the fake
     * cannot express.
     *
     * The legacy branch below reads `blob-meta` and then comes back to `refs`.
     * Written as two transactions it type-checks, passes every unit clause
     * against `fake-indexeddb.ts`, and throws in Chromium:
     *
     * ```
     * TransactionInactiveError: Failed to execute 'get' on 'IDBObjectStore':
     *   The transaction has finished.        at dm.rowFor
     * ```
     *
     * A real transaction auto-commits as soon as control returns to the event
     * loop with none of ITS OWN requests outstanding. Awaiting a request that
     * belongs to a different transaction is exactly that — so `tx` was finished
     * by the time the second `refs.get` was issued, and every legacy
     * digest-only reference failed to resolve in a real browser while the unit
     * suite stayed green.
     *
     * Awaiting requests of the SAME transaction is safe and is what this does:
     * the success handler runs with the transaction still active, and the
     * microtask an `await` schedules runs before control returns to the loop.
     *
     * Found by `e2e/typed-references.mjs` on its first real run, which is the
     * whole argument for that harness existing: the fake keeps its rows in a
     * `Map` and has no transaction lifetime to get wrong.
     */
    const tx = this.db.transaction([BLOB_REFS, BLOB_META], 'readonly');
    const refs = tx.objectStore(BLOB_REFS);
    // The composite key is STORAGE, not contract — a caller compares refs, and
    // an existing clause asserts `stat` returns exactly `{ id, mime, bytes }`.
    const plain = plainRef;
    if (typeof at !== 'string') {
      return plain((await promisify(refs.get(refKey(at.id, at.mime)))) as never);
    }
    /**
     * A LEGACY bare digest resolves through the row `blob-meta` recorded, not
     * through whichever typed row sorts first.
     *
     * @Codex, P2 on `8e4315a`: *"this `getAll()` result is ordered by the new
     * composite key rather than insertion time. For example, bytes originally
     * stored as `video/mp4` and later accepted as `audio/mp4` make the bare
     * digest resolve through the alphabetically earlier audio row,
     * retroactively changing the meaning of the existing legacy reference."*
     *
     * Which is decision `0132` round one all over again, reintroduced by the
     * fix for round two — and worse, it made the two backends disagree: the
     * memory store scans its rows in INSERTION order, so it kept the original
     * interpretation while this one silently swapped it.
     *
     * `blob-meta` is the answer and it was already there. `0047` keeps it for
     * exactly this — *"Legacy digest-only references resolve through their
     * existing stored type during migration"* — so it is the record of which
     * typed row a bare digest was ever allowed to mean. `put` maintains it for
     * digests first seen after the upgrade, so this is not only a migration
     * path.
     */
    const legacy = (await promisify(tx.objectStore(BLOB_META).get(at))) as
      BlobRef | undefined;
    if (!legacy) return undefined;
    // Through the typed row, so a bare reference cannot outlive the row it
    // names — deleting the pair must unresolve the legacy reference too.
    const typed = plain((await promisify(refs.get(refKey(at, legacy.mime)))) as never);
    if (typed) return typed;
    /**
     * The DIGEST-ONLY compatibility path, for a historical type the scheme
     * cannot encode.
     *
     * The v5 migration skips such a row rather than publishing a typed
     * reference nothing can select — @Codex, P2 on `857dbfa`. Measured after
     * making that change: the bytes then resolved through NOTHING, because
     * this branch requires the typed row it had just been denied. A person's
     * file becoming unreachable is a worse outcome than an odd string in a
     * metadata record, so the fall-back is here.
     *
     * Narrow on purpose. It fires only when `blob-meta` holds the digest and
     * `blob-refs` does not hold its pair, which after v5 means exactly one
     * thing: a type the migration refused. `list()` still never offers it, and
     * `stat({ id, mime })` still cannot select it — both were the finding.
     * What a caller gets back here it asked for by a legacy identity, which is
     * the identity that has always resolved this way.
     *
     * **Settled by @Codex on 2026-08-30**, in the amendment to `0047`: the
     * alternative reading — that such bytes should be unreachable until
     * re-ingested — is rejected outright. *"The typed scheme governs new
     * authority; it is not a retroactive deletion rule for bytes already
     * accepted by the previous public store boundary."*
     *
     * The amendment also names the KEYING, which is the part a later edit is
     * most likely to get wrong: *"The fallback must be keyed by the
     * legacy-reference shape and the absence of a typed pair, not by catching
     * arbitrary typed-resolution failures. A typed miss must remain a miss."*
     * That is the early return above — a typed lookup never reaches this line —
     * and `blobs.test.ts` pins it with a CANONICAL typed lookup for a digest
     * whose bytes are right here, which must still answer nothing. Measured:
     * turning this into a retry-bare fallback reddens five clauses.
     */
    return plain(legacy as never);
  }

  async get(at: BlobLookup): Promise<StoredBlob | undefined> {
    // The ROW first: it decides both whether this pair was accepted and which
    // type the answer carries, and a bare digest cannot be resolved without it.
    const meta = await this.rowFor(at);
    if (!meta) return undefined;
    const tx = this.db.transaction([BLOB_BYTES], 'readonly');
    const row = await promisify(tx.objectStore(BLOB_BYTES).get(meta.id));
    // Asserted as ArrayBuffer-backed rather than copied. IndexedDB returns a
    // structured clone, and structured clone produces a plain `ArrayBuffer`;
    // there is no path by which a record read here is backed by a
    // `SharedArrayBuffer`. The alternative is copying every byte of a file that
    // may be 200MB to restate something the platform already guarantees.
    const bytes = (row as { bytes?: Uint8Array<ArrayBuffer> } | undefined)?.bytes;
    if (!bytes || !meta) return undefined;
    /**
     * REVALIDATE THE ROW AFTER THE BYTES — @Codex, P2 on `7cc5d2c`. The row
     * and the bytes are two transactions, and between them another tab can
     * delete this digest and restore the same bytes under a different type;
     * the byte read then succeeds while the metadata in hand describes a
     * typed pair that no longer exists, so a DELETED typed selector resolved.
     * Decision 0047's boundary is the typed pair, so the pair is asked again
     * once the bytes are in hand — one metadata read, no byte copy — and a
     * pair that vanished is a miss, exactly as a fresh lookup would answer.
     * (Not one transaction over both stores: `rowFor` awaits its own
     * requests, and this file has already paid once for a foreign await
     * ending a live transaction.)
     */
    const still = await this.rowFor(at);
    if (!still || still.id !== meta.id || still.mime !== (meta as BlobRef).mime) {
      return undefined;
    }
    return { bytes, mime: (meta as BlobRef).mime };
  }

  async stat(at: BlobLookup): Promise<BlobRef | undefined> { return this.rowFor(at); }

  /**
   * `0047`: *"delete(id) retains its present administrative meaning: delete the
   * byte record and every typed metadata row for that digest."* Not a
   * delete-one-type operation, and no caller may read it as reference counting.
   */
  async delete(id: BlobId): Promise<void> {
    const tx = this.db.transaction([BLOB_BYTES, BLOB_META, BLOB_REFS], 'readwrite');
    tx.objectStore(BLOB_BYTES).delete(id);
    tx.objectStore(BLOB_META).delete(id);
    const refs = tx.objectStore(BLOB_REFS);
    const all = ((await promisify(refs.getAll())) ?? []) as (BlobRef & { key: string })[];
    for (const r of all) if (r.id === id) refs.delete(r.key);
    await settled(tx);
  }

  /**
   * `0047`: *"list() lists accepted typed rows, so one digest may appear more
   * than once with different media types."* Still the metadata store only —
   * reading `blob-bytes` would answer "what is in here" by loading everything
   * in it, which is the question these stores are split so nobody has to.
   */
  async list(): Promise<readonly BlobRef[]> {
    const tx = this.db.transaction([BLOB_REFS], 'readonly');
    const all = ((await promisify(tx.objectStore(BLOB_REFS).getAll())) ?? []) as
      (BlobRef & { key?: string })[];
    return all.map((r) => plainRef(r)!);
  }
}

/**
 * A stored row as the PUBLIC reference, with the composite storage key gone.
 *
 * `blob-refs` is keyed by `(id, mime)` and carries that key as a field, so a
 * row read back is `{ key, id, mime, bytes }` while `put`'s first return, the
 * memory backend, `stat` and `list` are all `{ id, mime, bytes }`.
 *
 * @Codex, P2 on #184: *"callers that enumerate, spread, serialize, or compare
 * the public reference observe an extra backend-specific `key` depending on
 * whether the row already existed."* The tell is *depending on* — the same
 * `put` returned two different shapes for the same bytes according to whether
 * anybody had stored them before, which is the one difference a caller has no
 * way to anticipate.
 *
 * Module-level because it was three local spellings and is now four. Three
 * copies of "the composite key is storage, not contract" is how the second
 * storage field arrives in a reference: `rowFor` and `list` both had it and
 * `put`'s repeated-write branch, added later, did not.
 */
function plainRef(r: (BlobRef & { key?: string }) | undefined): BlobRef | undefined {
  return r ? { id: r.id, mime: r.mime, bytes: r.bytes } : undefined;
}

/** Resolve on `oncomplete` rather than on the last request's `onsuccess`, which
 *  fires before the transaction has committed — the same rule `commit` states,
 *  extracted because it is now stated in five places. */
function settled(tx: IdbTransactionLike): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    const fail = () => reject(new Error(tx.error?.message ?? 'IndexedDB transaction failed'));
    tx.onerror = fail;
    tx.onabort = fail;
  });
}

export interface IndexedDbOptions extends StoreOptions {
  readonly dbName: string;
  /** Injected so the adapter is testable off a browser. Defaults to the global
   *  when one exists, which is the only thing a browser caller has to know. */
  readonly factory?: IdbFactoryLike;
}

/**
 * The blob half of an IndexedDB store, opened on its own.
 *
 * Separate from `openIndexedDbStore` because the two have different lifetimes:
 * a host may want bytes before it has hydrated a graph, and hydration reads
 * every object. Same database, same schema, same upgrade path — so opening
 * either one creates both sets of stores and the other finds them there.
 */
export async function openIndexedDbBlobs(o: IndexedDbOptions): Promise<BlobStore> {
  const factory = o.factory
    ?? (globalThis as { indexedDB?: IdbFactoryLike }).indexedDB;
  if (!factory) {
    throw new Error(
      'no IndexedDB factory: pass one as `factory`, or run where globalThis.indexedDB exists',
    );
  }
  return (await IndexedDbPersistence.open(factory, o.dbName)).blobs();
}

export async function openIndexedDbStore(o: IndexedDbOptions): Promise<ObjectStore> {
  const factory = o.factory
    ?? (globalThis as { indexedDB?: IdbFactoryLike }).indexedDB;
  if (!factory) {
    throw new Error(
      'no IndexedDB factory: pass one as `factory`, or run where globalThis.indexedDB exists',
    );
  }
  const persistence = await IndexedDbPersistence.open(factory, o.dbName);
  const engine = new StoreEngine('indexeddb', persistence, o);
  await engine.hydrate(o);
  return engine;
}
